import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, Archive, MapPin, FileText, Copy, Loader2, RefreshCw, Trash2, Eye, Edit2, Database } from 'lucide-react';
import { newslettersApi, NewsletterRecord, extractLookupDisplay, submissionsApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Newsletters() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [newsletters, setNewsletters] = useState<NewsletterRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fetchNewsletters = async () => {
    setLoading(true);
    try {
      const data = await newslettersApi.list();
      setNewsletters(data);
    } catch (err) {
      console.error('Failed to fetch newsletters:', err);
      toast.error('Failed to load newsletters');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchNewsletters();
  }, []);
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      // Check for linked submissions before deleting
      const newsletter = newsletters.find(n => n.ID === id);
      let submissionCount = 0;
      if (newsletter?.Newsletter_ID) {
        try {
          const linkedSubs = await submissionsApi.findByNewsletterId(newsletter.Newsletter_ID);
          submissionCount = linkedSubs.length;
        } catch {
          // Non-blocking — proceed with delete
        }
      }
      await newslettersApi.delete(id);
      if (submissionCount > 0) {
        toast.success('Newsletter deleted', {
          description: `Note: ${submissionCount} submission(s) still reference this newsletter's ID. They are preserved as historical records.`,
          duration: 8000
        });
      } else {
        toast.success('Newsletter deleted');
      }
      setDeleteConfirm(null);
      fetchNewsletters();
    } catch (err) {
      console.error('Failed to delete newsletter:', err);
      toast.error('Failed to delete newsletter');
    } finally {
      setIsDeleting(false);
    }
  };
  // Filter by team and region
  const filteredNewsletters = newsletters.filter(n => {
    // 1. Filter by Team
    if (n.Team !== currentWorkspace.name) return false;
    // 2. Filter by Region
    // If currentSubRegion is set, show records matching that region OR global records (empty region)
    // If currentSubRegion is NOT set (All Regions), show everything
    if (currentSubRegion) {
      return !n.Region || n.Region === currentSubRegion;
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-3638">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-3639">
        <div data-id="element-3640">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3641">
            Newsletters & Lists
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3642">
            Manage audience lists and configuration for your campaigns.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-3643">
          <button onClick={fetchNewsletters} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-3644">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-3645" />
          </button>
          <Link to="/newsletters/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3646">
            <Plus className="h-4 w-4" data-id="element-3647" />
            Create Newsletter
          </Link>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3648">
        {loading && newsletters.length === 0 ? <div className="p-12 text-center" data-id="element-3649">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-3650" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-3651">Loading newsletters...</p>
          </div> : filteredNewsletters.length === 0 ? <div className="p-12 text-center" data-id="element-3652">
            <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-3653" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-3654">
              No newsletters found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-3655">
              Create your first newsletter list to start collecting subscribers.
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-3656">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3657">
              <thead className="bg-gray-50" data-id="element-3658">
                <tr data-id="element-3659">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3660">
                    Newsletter Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3661">
                    Signup Form
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3662">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3663">
                    Mailing Lists
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3664">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3665">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3666">
                {filteredNewsletters.map(newsletter => <tr key={newsletter.ID} className="hover:bg-gray-50" data-id="element-3667">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3668">
                      <div className="text-sm font-medium text-gray-900" data-id="element-3669">
                        {newsletter.Newsletter}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3670">
                      {newsletter.Sign_Up_Form ? <span className="inline-flex items-center gap-1 text-sm text-gray-600" data-id="element-3671">
                          <FileText className="h-3.5 w-3.5" data-id="element-3672" />
                          {extractLookupDisplay(newsletter.Sign_Up_Form)}
                        </span> : <span className="text-sm text-gray-400" data-id="element-3673">
                          No form linked
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3674">
                      {newsletter.Region ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-3675">
                          <MapPin className="h-3 w-3" data-id="element-3676" />
                          {newsletter.Region}
                        </span> : <span className="text-xs text-gray-500 italic" data-id="element-3677">
                          All Regions
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3678">
                      {newsletter.Mailing_Lists && newsletter.Mailing_Lists.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-3679">
                          {newsletter.Mailing_Lists.slice(0, 2).map((l, i) => <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-3680">
                              <Database className="h-3 w-3" data-id="element-3681" />
                              {l.Mailing_List_Name}
                            </span>)}
                          {newsletter.Mailing_Lists.length > 2 && <span className="text-xs text-gray-400" data-id="element-3682">
                              +{newsletter.Mailing_Lists.length - 2} more
                            </span>}
                        </div> : <span className="text-sm text-gray-400" data-id="element-3683">-</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3684">
                      {newsletter.Newsletter_Tags && newsletter.Newsletter_Tags.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-3685">
                          {newsletter.Newsletter_Tags.slice(0, 3).map((t, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-3686">
                                {t.Tag_Name}
                              </span>)}
                          {newsletter.Newsletter_Tags.length > 3 && <span className="text-xs text-gray-400" data-id="element-3687">
                              +{newsletter.Newsletter_Tags.length - 3} more
                            </span>}
                        </div> : <span className="text-sm text-gray-400" data-id="element-3688">-</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3689">
                      <div className="flex items-center justify-end gap-2" data-id="element-3690">
                        <Link to={`/newsletters/${newsletter.ID}`} className="text-gray-400 hover:text-blue-600 p-2 hover:bg-blue-50 rounded" title="View Details" data-id="element-3691">
                          <Eye className="h-4 w-4" data-id="element-3692" />
                        </Link>
                        <Link to={`/newsletters/${newsletter.ID}/edit`} className="text-gray-400 hover:text-blue-600 p-2 hover:bg-blue-50 rounded" title="Edit" data-id="element-3693">
                          <Edit2 className="h-4 w-4" data-id="element-3694" />
                        </Link>
                        <button onClick={() => setDeleteConfirm(newsletter.ID)} className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-3695">
                          <Trash2 className="h-4 w-4" data-id="element-3696" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3697">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3698">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-3699" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-3700">
              <div className="flex items-center gap-4 mb-4" data-id="element-3701">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-3702">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-3703" />
                </div>
                <div data-id="element-3704">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-3705">
                    Delete Newsletter
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-3706">
                    Are you sure? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-3707">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3708">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-3709">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3710" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}